// src/components/roles/RolesTable/RolesTable.hooks.ts
import { useCallback, useState, useRef } from 'react';
import { Role } from '../../../types/role.types';
import { isSystemRole } from '../../../types/role.types';

export interface UseRolesTableProps {
  roles: Role[];
  selectedRoles: string[];
  setSelectedRoles: React.Dispatch<React.SetStateAction<string[]>>;
  onViewRole?: (id: string) => void;
  onEditRole?: (id: string) => void;
  onDeleteRole?: (id: string) => void;
  onCopyRole?: (id: string) => void;
  onBlockRole?: (id: string, isBlocked: boolean) => void;
  onBlockRoleDirect?: (id: string) => Promise<void>; // Para bloquear directamente desde el switch
  onUnblockRoleDirect?: (id: string) => Promise<void>; // Para desbloquear directamente desde el switch
  isSuperAdmin?: boolean; // Para mostrar/ocultar botón eliminar
}

export const useRolesTable = ({
  roles,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedRoles,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelectedRoles,
  onViewRole,
  onEditRole,
  onDeleteRole,
  onCopyRole,
  onBlockRole,
  onBlockRoleDirect,
  onUnblockRoleDirect,
  isSuperAdmin = false,
}: UseRolesTableProps) => {
  // Estado para el popover de confirmación de bloqueo/desbloqueo
  const [confirmingBlockRoleId, setConfirmingBlockRoleId] = useState<string | null>(null);
  const [confirmingAction, setConfirmingAction] = useState<'block' | 'unblock' | null>(null);
  const [blockingRoleId, setBlockingRoleId] = useState<string | null>(null);
  const switchAnchorRefs = useRef<Record<string, HTMLElement>>({});
  // Status helpers
  const getStatusDisplay = useCallback((isActive: boolean) => {
    return isActive ? 'Activo' : 'Bloqueado';
  }, []);

  const getStatusColor = useCallback((isActive: boolean) => {
    return isActive ? 'success' : 'error';
  }, []);

  // Check if role is system role
  const checkIsSystemRole = useCallback((role: Role) => {
    return isSystemRole(role);
  }, []);

  // Format user count
  const formatUserCount = useCallback((count: number) => {
    return count.toString();
  }, []);

  // Format date with time
  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  }, []);

  // Action handlers
  const handleViewRole = useCallback(
    (id: string) => {
      if (onViewRole) {
        onViewRole(id);
      }
    },
    [onViewRole],
  );

  const handleEditRole = useCallback(
    (id: string) => {
      if (onEditRole) {
        onEditRole(id);
      }
    },
    [onEditRole],
  );

  const handleDeleteRole = useCallback(
    (id: string) => {
      if (onDeleteRole) {
        onDeleteRole(id);
      }
    },
    [onDeleteRole],
  );

  const handleCopyRole = useCallback(
    (id: string) => {
      if (onCopyRole) {
        onCopyRole(id);
      }
    },
    [onCopyRole],
  );

  // Check if role can be deleted (only Super Admin and not system role)
  const canDeleteRole = useCallback(
    (role: Role) => {
      return isSuperAdmin && !checkIsSystemRole(role);
    },
    [isSuperAdmin, checkIsSystemRole],
  );

  // Check if role can be edited (not system role, or Super Admin can edit some permissions)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEditRole = useCallback((role: Role) => {
    // System roles can be edited by Super Admin (for permissions), but we'll handle this in the drawer
    return true; // Always allow edit, validation will be in the drawer
  }, []);

  // Handler para cuando se hace clic en el switch
  const handleSwitchChange = useCallback((role: Role, newValue: boolean, anchorEl: HTMLElement) => {
    // Si está bloqueando, mostrar confirmación
    if (!newValue && role.isActive) {
      switchAnchorRefs.current[role.id] = anchorEl;
      setConfirmingBlockRoleId(role.id);
      setConfirmingAction('block');
      return;
    }

    // Si está desbloqueando, también mostrar confirmación
    if (newValue && !role.isActive) {
      switchAnchorRefs.current[role.id] = anchorEl;
      setConfirmingBlockRoleId(role.id);
      setConfirmingAction('unblock');
      return;
    }
  }, []);

  // Handler para confirmar el bloqueo/desbloqueo
  const handleConfirmBlock = useCallback(
    (roleId: string) => {
      const action = confirmingAction;
      setConfirmingBlockRoleId(null);
      setConfirmingAction(null);
      setBlockingRoleId(roleId);

      if (action === 'block') {
        // Bloquear
        if (onBlockRoleDirect) {
          onBlockRoleDirect(roleId)
            .then(() => {
              setBlockingRoleId(null);
            })
            .catch(() => {
              setBlockingRoleId(null);
            });
        } else if (onBlockRole) {
          onBlockRole(roleId, true); // true = bloquear
          setTimeout(() => {
            setBlockingRoleId(null);
          }, 2000);
        }
      } else if (action === 'unblock') {
        // Desbloquear
        if (onUnblockRoleDirect) {
          onUnblockRoleDirect(roleId)
            .then(() => {
              setBlockingRoleId(null);
            })
            .catch(() => {
              setBlockingRoleId(null);
            });
        } else if (onBlockRole) {
          onBlockRole(roleId, false); // false = desbloquear
          setTimeout(() => {
            setBlockingRoleId(null);
          }, 2000);
        }
      }
    },
    [confirmingAction, onBlockRole, onBlockRoleDirect, onUnblockRoleDirect],
  );

  // Handler para cancelar el bloqueo/desbloqueo
  const handleCancelBlock = useCallback(() => {
    setConfirmingBlockRoleId(null);
    setConfirmingAction(null);
  }, []);

  // Verificar si un rol está siendo bloqueado
  const isBlocking = useCallback(
    (roleId: string) => {
      return blockingRoleId === roleId;
    },
    [blockingRoleId],
  );

  // Verificar si un rol tiene el popover abierto
  const isConfirmingBlock = useCallback(
    (roleId: string) => {
      return confirmingBlockRoleId === roleId;
    },
    [confirmingBlockRoleId],
  );

  // Obtener el anchor element para el popover
  const getSwitchAnchor = useCallback((roleId: string) => {
    return switchAnchorRefs.current[roleId] || null;
  }, []);

  // Obtener el mensaje de confirmación según la acción
  const getConfirmationMessage = useCallback(
    (roleId: string) => {
      const role = roles.find((r) => r.id === roleId);
      if (!role) return '¿Desea cambiar el estado de este rol?';

      if (confirmingAction === 'block') {
        return '¿Desea bloquear este rol?';
      } else if (confirmingAction === 'unblock') {
        return '¿Desea desbloquear este rol?';
      }
      return '¿Desea cambiar el estado de este rol?';
    },
    [roles, confirmingAction],
  );

  // Obtener el tipo de acción para el popover
  const getActionType = useCallback(() => {
    return confirmingAction;
  }, [confirmingAction]);

  return {
    // Status helpers
    getStatusDisplay,
    getStatusColor,

    // Role helpers
    checkIsSystemRole,
    formatUserCount,
    formatDate,

    // Actions
    handleViewRole,
    handleEditRole,
    handleDeleteRole,
    handleCopyRole,

    // Permissions
    canDeleteRole,
    canEditRole,

    // Block handlers
    handleSwitchChange,
    handleConfirmBlock,
    handleCancelBlock,
    isBlocking,
    isConfirmingBlock,
    getSwitchAnchor,
    getConfirmationMessage,
    getActionType,
  };
};
